import type { BootLoaderPort } from "./loader/BootLoaderPort";
import type { RetryState } from "./retry/RetryWithBackoff";

/** The boot's stages in order; each owns a fraction of the loader bar and a status line. */
export type BootStage = "connecting" | "profile" | "bundles" | "branding" | "branded" | "opening";

const STAGES: Record<BootStage, { fraction: number; status: string }> = {
  connecting: { fraction: 0.01, status: "Connecting..." },
  profile: { fraction: 0.42, status: "Detecting device profile..." },
  bundles: { fraction: 0.5, status: "Loading content..." },
  branding: { fraction: 0.76, status: "Resolving operator brand..." },
  branded: { fraction: 0.8, status: "Resolving operator brand..." },
  opening: { fraction: 0.82, status: "Opening game..." },
};

/** The bundles stage fills the bar from its own fraction up to the branding stage's. */
const BUNDLES_SPAN = STAGES.branding.fraction - STAGES.bundles.fraction - 0.04;

/** The loader as the boot reports to it: a stage, the bundle progress inside the bundles stage, a retry. The debug line shows in dev only. */
export class BootProgress {
  public constructor(
    private readonly loader: BootLoaderPort | null,
    private readonly showDebug: boolean,
  ) {}

  public stage(stage: BootStage, debug?: string): void {
    const s = STAGES[stage];
    this.report(s.fraction, s.status, debug);
  }

  /** `fraction01` of the core bundles loaded. */
  public bundles(fraction01: number, debug?: string): void {
    this.report(STAGES.bundles.fraction + Math.max(0, Math.min(1, fraction01)) * BUNDLES_SPAN, STAGES.bundles.status, debug);
  }

  public retrying(task: string, state: RetryState): void {
    this.report(null, "Retrying...", `${task}: attempt=${state.attempt} next=${state.nextDelayMs}ms`);
  }

  private report(fraction: number | null, status: string, debug?: string): void {
    if (!this.loader) return;
    if (fraction !== null) this.loader.setProgress01(fraction);
    this.loader.setStatusText(status);
    if (debug !== undefined) this.loader.setDebugText(this.showDebug ? debug : null);
  }
}
